import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Clock, Trophy, Plus, Users, ChevronDown, X, Check, MoreVertical, UserMinus, AlertTriangle, Crown, Shield, UserX, Armchair } from 'lucide-react';
import { api, parseApiError, type ClanDetail, type ClanMember, type ClanScopedRole, type Delegation, type Me, type BannedMember, type ClanSeat } from '../lib/api';
import { MakeCaptainModal, DURATION_OPTIONS } from './MakeCaptainModal';
import { captainKindLabel, captainKindBadge, captainExpiryDisplay } from '../lib/roles';
import { canManageTeamScoped, canAssignToTeam, canUnassignFromTeam } from '../lib/captainAccess';
import { cn } from '../lib/cn';
import { ConfirmModal } from './ConfirmModal';

type Toast = { kind: 'removed' | 'role' | 'error'; msg: string; undo?: () => void } | null;

function relativeExpiry(expiresAt: number): string {
  const diff = expiresAt - Date.now();
  if (diff <= 0) return 'expired';
  const h = Math.floor(diff / 3_600_000);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function memberName(m: ClanMember): string {
  return m.gamertag ?? m.displayName ?? shortId(m.discordId);
}

function resolveName(discordId: string, members: ClanMember[]): string {
  const m = members.find(x => x.discordId === discordId);
  return m ? memberName(m) : shortId(discordId);
}

// ── Squad Board ───────────────────────────────────────────────────────────────
// Displays members grouped by teamNo, with per-team captain management and a
// bench row for unassigned players. All read data is derived from clan props —
// no extra API calls. Write operations: setMemberTeam + mintDelegation (via
// MakeCaptainModal) + revokeDelegation + updateClanMemberRole + removeClanMember.

export function ClanTeamsBoard({
  clan,
  me,
  canManage,
  canManageRoles = false,
  isClanOwner = false,
  viewerCaptainTeams = [],
  onToast,
}: {
  clan: ClanDetail;
  me: Me;
  canManage: boolean;
  canManageRoles?: boolean;
  isClanOwner?: boolean;
  // Delegation-visibility audit (T4) — teamNos the viewer captains. Widens
  // "Remove from Clan" (per member, own team) and "Add Seat" (per team
  // card, own team, forces teamNo — the call already hardcodes this card's
  // own teamNo, so there's no picker to restrict). Own-team seat/bench
  // assignment (2026-07-21) ALSO widens: "+ Add Player" (destination = this
  // card's team) and the per-member "Move to Bench" action (source = this
  // card's team) — both via canAssignToTeam/canUnassignFromTeam, mirroring
  // the backend's PATCH .../members/:id/team overlay exactly. Promote/
  // Demote, Ban, Transfer Ownership, Set/Revoke Captain and Create Team stay
  // gated on canManage/canManageRoles/isClanOwner exactly as before — none
  // of those routes are team-scoped-delegation-eligible.
  viewerCaptainTeams?: number[];
  onToast: (t: Toast) => void;
}) {
  // ── ALL HOOKS AT THE TOP — no early returns before this block ──────────────
  const qc = useQueryClient();

  // Local UI state
  // Inline "new team" draft — an expanding segment in the board (not a popup):
  // name it, pick who plays on it, create. Picked players fill the team;
  // everyone left unpicked stays in the clan on the bench (no team, no seat).
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());
  const [captainModal, setCaptainModal] = useState<{ member: ClanMember; teamNo: number } | null>(null);
  const [addPlayerTeam, setAddPlayerTeam] = useState<number | null>(null);
  const [addSeatTeam, setAddSeatTeam] = useState<number | null>(null);
  const [addSeatGamertag, setAddSeatGamertag] = useState('');
  const [captainPickerTeam, setCaptainPickerTeam] = useState<number | null>(null);
  const [benchAssignId, setBenchAssignId] = useState<string | null>(null);
  // Chip ⋮ menu — single openMenuId avoids per-chip hooks inside .map()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Extend-captain-window popover — same single-open-id idiom as the chip menu.
  const [extendGrantId, setExtendGrantId] = useState<string | null>(null);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<ClanMember | null>(null);
  const [confirmTransferMember, setConfirmTransferMember] = useState<ClanMember | null>(null);
  const [transferAcknowledged, setTransferAcknowledged] = useState(false);
  const [confirmBanMember, setConfirmBanMember] = useState<ClanMember | null>(null);
  const [banReason, setBanReason] = useState('');
  // Ref points to the currently-open chip's menu wrapper (re-assigned each render)
  const chipMenuRef = useRef<HTMLDivElement>(null);
  const extendMenuRef = useRef<HTMLDivElement>(null);

  // Click-outside for chip menus — single document listener, no per-chip hooks
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      if (!chipMenuRef.current?.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  // Click-outside for the extend popover — same idiom as the chip menu above.
  useEffect(() => {
    if (!extendGrantId) return;
    const handler = (e: MouseEvent) => {
      if (!extendMenuRef.current?.contains(e.target as Node)) setExtendGrantId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [extendGrantId]);

  // NOTE: all clan mutations below invalidate the broad ['clan'] key, not
  // ['clan', clan.name]. The parent (LeaderPanel) keys its clan query by
  // me.clanName (canonical slug), whose CASE can differ from this clan.name
  // prop — an exact-key invalidate silently misses and the UI stays stale
  // until a manual refresh. Same guard GhostSeatManager uses.
  const teamMut = useMutation({
    mutationFn: ({ discordId, teamNo }: { discordId: string; teamNo: number | null }) =>
      api.setMemberTeam(clan.name, discordId, teamNo),
    onSuccess: (_data, { teamNo }) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      const label = teamNo != null ? `Team ${teamNo}` : 'bench';
      onToast({ kind: 'role', msg: `Moved to ${label}.` });
      setAddPlayerTeam(null);
      setBenchAssignId(null);
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const createTeamMut = useMutation({
    mutationFn: () =>
      api.createTeam(clan.name, { name: draftName.trim(), memberIds: Array.from(draftIds) }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      onToast({ kind: 'role', msg: `Team "${data.name}" created with ${data.memberIds.length} player${data.memberIds.length === 1 ? '' : 's'}.` });
      // Reset the draft but keep it open: the refetch bumps nextTeamNo, so the
      // same segment becomes a fresh "create the next team" form. If all 10
      // team slots are now used, nextTeamNo goes null and the segment hides.
      setDraftName('');
      setDraftIds(new Set());
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const createSeatMut = useMutation({
    mutationFn: ({ gamertag, teamNo }: { gamertag: string; teamNo: number }) =>
      api.createGhostSeat(clan.name, gamertag, undefined, teamNo),
    onSuccess: (_data, { teamNo }) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      onToast({ kind: 'role', msg: `Seat reserved on Team ${teamNo}.` });
      setAddSeatTeam(null);
      setAddSeatGamertag('');
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const revokeMut = useMutation({
    mutationFn: ({ granteeId, grantId }: { granteeId: string; grantId: string }) =>
      api.revokeDelegation(clan.name, granteeId, grantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      onToast({ kind: 'role', msg: 'Captain revoked.' });
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  // Extend a WINDOWED grant's expiry FROM NOW by the chosen preset — mirrors
  // CaptainsPanel's identical affordance (both surfaces render a captain
  // Revoke button, so both get Extend; api.extendDelegation was dead code
  // until this UI, audit finding). CLAN_OWNER-only server-side, same gate
  // canManage already reflects here — no new permission surface.
  const extendMut = useMutation({
    mutationFn: ({ granteeId, grantId, hours }: { granteeId: string; grantId: string; hours: number }) =>
      api.extendDelegation(clan.name, granteeId, grantId, hours),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      onToast({ kind: 'role', msg: 'Captain window extended.' });
      setExtendGrantId(null);
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const roleMut = useMutation({
    mutationFn: ({ discordId, scopedRole }: { discordId: string; scopedRole: ClanScopedRole }) =>
      api.updateClanMemberRole(clan.name, discordId, scopedRole),
    onSuccess: (_data, { scopedRole }) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      const label =
        scopedRole === 'CLAN_OWNER'
          ? 'made clan leader — ownership transferred'
          : scopedRole === 'CLAN_LEADER'
          ? 'promoted to co-admin'
          : 'set to member';
      onToast({ kind: 'role', msg: `Done: ${label}.` });
      setConfirmTransferMember(null);
      setTransferAcknowledged(false);
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const removeMut = useMutation({
    mutationFn: (discordId: string) => api.removeClanMember(clan.name, discordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      onToast({ kind: 'removed', msg: `Member removed from ${clan.name}.` });
      setConfirmRemoveMember(null);
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const banMut = useMutation({
    mutationFn: ({ discordId, reason }: { discordId: string; reason?: string }) =>
      api.banClanMember(clan.name, discordId, reason),
    onSuccess: (_data, { discordId }) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      const name = confirmBanMember ? memberName(confirmBanMember) : discordId;
      onToast({ kind: 'removed', msg: `${name} banned from ${clan.name}.` });
      setConfirmBanMember(null);
      setBanReason('');
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const unbanMut = useMutation({
    mutationFn: (discordId: string) => api.unbanClanMember(clan.name, discordId),
    onSuccess: (_data, discordId) => {
      qc.invalidateQueries({ queryKey: ['clan'] });
      const banned = clan.bannedMembers ?? [];
      const entry = banned.find(b => b.discordId === discordId);
      const name = entry?.gamertag ?? shortId(discordId);
      onToast({ kind: 'role', msg: `${name} unbanned — they can rejoin ${clan.name}.` });
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  // ── Derived data ───────────────────────────────────────────────────────────

  // One delegation per team number (first active wins)
  const delegations = clan.activeDelegations ?? [];
  const delegationByTeam = new Map<number, Delegation>();
  for (const d of delegations) {
    if (d.teamNo != null && !delegationByTeam.has(d.teamNo)) {
      delegationByTeam.set(d.teamNo, d);
    }
  }

  // Group members by teamNo; bench = no teamNo
  const membersByTeam = new Map<number, ClanMember[]>();
  const benchMembers: ClanMember[] = [];
  for (const m of clan.members) {
    // Leader is never a benchable/teamable player — skip them from all groupings
    if (m.discordId === clan.leaderId || m.role === 'LEADER') continue;
    if (m.teamNo) {
      const arr = membersByTeam.get(m.teamNo) ?? [];
      arr.push(m);
      membersByTeam.set(m.teamNo, arr);
    } else {
      benchMembers.push(m);
    }
  }

  // Reserved seats (ghost/pending) parked on a team — grouped by teamNo. These
  // are placeholders: no member yet, but claiming them (any path) auto-joins
  // this team.
  const reservedSeatsByTeam = new Map<number, ClanSeat[]>();
  for (const s of clan.seats) {
    if ((s.status === 'GHOST' || s.status === 'PENDING_CLAIM') && s.teamNo != null) {
      const arr = reservedSeatsByTeam.get(s.teamNo) ?? [];
      arr.push(s);
      reservedSeatsByTeam.set(s.teamNo, arr);
    }
  }

  // Named-but-possibly-empty teams (created with no roster yet)
  const namedTeamNos = Object.keys(clan.teamNames ?? {})
    .map(Number)
    .filter(n => !Number.isNaN(n));

  // Sorted list of team numbers to render: anything with a member, an active
  // captain, a name, or a reserved seat — so an empty or reserved-only team
  // still shows up as a card instead of disappearing.
  const usedTeamNos = Array.from(
    new Set([
      ...membersByTeam.keys(),
      ...delegationByTeam.keys(),
      ...namedTeamNos,
      ...reservedSeatsByTeam.keys(),
    ])
  ).sort((a, b) => a - b);

  // First unused team number (1–10), null if all full
  const nextTeamNo = (() => {
    const used = new Set(usedTeamNos);
    for (let i = 1; i <= 10; i++) {
      if (!used.has(i)) return i;
    }
    return null;
  })();

  // Teams to render: existing + ephemeral new-team slot if pending
  const displayTeamNos: number[] = [...usedTeamNos];

  // Team numbers available in the bench assign picker
  const assignTeamOptions: number[] = nextTeamNo
    ? [...usedTeamNos, nextTeamNo]
    : usedTeamNos;

  // Own-team seat/bench assignment (2026-07-21): a captain sees ONLY their
  // own team(s) as a bench-assign destination — canAssignToTeam mirrors the
  // backend's destination-only check, filtering additively over
  // assignTeamOptions (canManage still sees the full list, unchanged; a
  // captain's teamNo can never equal nextTeamNo — that slot doesn't exist
  // yet — so the "create the next team via bench-assign" path stays
  // canManage-only automatically, no separate exclusion needed).
  const benchAssignableTeamNos = assignTeamOptions.filter(n => canAssignToTeam(canManage, viewerCaptainTeams, n));

  // ── Chip ⋮ menu renderer — not a component (no hooks), just a function ─────
  // Used for both team chips and bench chips. Returns null for the current user.
  function renderChipMenu(m: ClanMember) {
    // Delegation-visibility audit (T4): a captain of m's OWN team gets this
    // menu too — "Remove from Clan" AND (own-team-assignment item, 2026-07-21)
    // "Move to Bench" are reachable for them below — Promote/Demote/Ban/
    // Transfer stay gated on canManageRoles/isClanOwner (unaffected by this
    // widen). A bench member (teamNo null) never matches any captain grant,
    // same as the backend's own exclusion.
    if (!canManageTeamScoped(canManage, viewerCaptainTeams, m.teamNo) || m.discordId === me.discordId) return null;
    const isOpen = openMenuId === m.discordId;
    return (
      <div
        className="relative ml-1"
        ref={isOpen ? chipMenuRef : undefined}
      >
        <button
          onClick={() => setOpenMenuId(prev => prev === m.discordId ? null : m.discordId)}
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-sm border transition-colors',
            isOpen
              ? 'border-forge-500/70 bg-iron-800 text-white'
              : 'border-iron-600/70 bg-iron-950 text-iron-200 hover:border-iron-400 hover:bg-iron-800 hover:text-white active:bg-iron-800'
          )}
          aria-label="Member actions"
          aria-expanded={isOpen}
        >
          <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
        {isOpen && (
          <div className="absolute left-0 top-full z-40 mt-0.5 w-44 border border-iron-700/60 bg-iron-950 shadow-lg">
            <div className="border-b border-iron-800/40 px-3 py-1.5">
              <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                Actions
              </p>
            </div>
            <div className="py-1">
              {canManageRoles && (
                m.scopedRole === 'CLAN_LEADER' ? (
                  <button
                    onClick={() => { setOpenMenuId(null); roleMut.mutate({ discordId: m.discordId, scopedRole: 'CLAN_MEMBER' }); }}
                    disabled={roleMut.isPending}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white disabled:opacity-50"
                  >
                    <Shield className="h-3 w-3 text-iron-400" strokeWidth={2.5} />
                    Demote to Member
                  </button>
                ) : (
                  <button
                    onClick={() => { setOpenMenuId(null); roleMut.mutate({ discordId: m.discordId, scopedRole: 'CLAN_LEADER' }); }}
                    disabled={roleMut.isPending}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white disabled:opacity-50"
                  >
                    <Crown className="h-3 w-3 text-emerald-400" strokeWidth={2.5} />
                    Promote to Co-Admin
                  </button>
                )
              )}
              {/* Own-team seat/bench assignment (2026-07-21) — a captain may
                  send a member of THEIR OWN team back to the bench; mirrors
                  the backend's requestedTeamNo===null branch exactly
                  (canUnassignFromTeam === canManageTeamScoped's own
                  condition, so this is already covered by the outer gate
                  above whenever m.teamNo is set — the m.teamNo!=null check
                  just excludes bench members, who have nothing to unassign). */}
              {m.teamNo != null && canUnassignFromTeam(canManage, viewerCaptainTeams, m.teamNo) && (
                <button
                  onClick={() => { setOpenMenuId(null); teamMut.mutate({ discordId: m.discordId, teamNo: null }); }}
                  disabled={teamMut.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white disabled:opacity-50"
                >
                  <Armchair className="h-3 w-3 text-iron-400" strokeWidth={2.5} />
                  Move to Bench
                </button>
              )}
              <button
                onClick={() => { setOpenMenuId(null); setConfirmRemoveMember(m); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
              >
                <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                Remove from Clan
              </button>
              {isClanOwner && (
                <button
                  onClick={() => { setOpenMenuId(null); setConfirmBanMember(m); setBanReason(''); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-200 transition-colors hover:bg-red-950/60 hover:text-red-100 border-t border-iron-800/40"
                >
                  <UserX className="h-3 w-3 text-red-400" strokeWidth={2.5} />
                  Ban from Clan
                </button>
              )}
              {isClanOwner && (
                <button
                  onClick={() => { setOpenMenuId(null); setConfirmTransferMember(m); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-amber-300 transition-colors hover:bg-amber-950/40 hover:text-amber-200"
                >
                  <Crown className="h-3 w-3 text-amber-400" strokeWidth={2.5} />
                  Transfer Ownership
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Section header + New Team button */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          <Users className="h-4 w-4 text-forge-400" />
          Teams
        </h2>
        {canManage && nextTeamNo && !draftOpen && (
          <button
            onClick={() => setDraftOpen(true)}
            className="flex items-center gap-1.5 border border-iron-700/60 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500/50 hover:text-forge-200"
          >
            <Plus className="h-3 w-3" strokeWidth={2.5} />
            Create Team
          </button>
        )}
      </div>

      {/* Leader line — not shown in team/bench groupings */}
      {(() => {
        const leader = clan.members.find(m => m.discordId === clan.leaderId);
        return leader ? (
          <div className="mb-3 flex items-center gap-1.5 text-[10px] lg:text-xs text-iron-500">
            <span className="font-display font-black uppercase tracking-wider">Leader</span>
            <span className="text-iron-400">{memberName(leader)}</span>
          </div>
        ) : null;
      })()}

      {/* Team cards */}
      <div className="mb-6 space-y-3">
        {displayTeamNos.map(teamNo => {
          const members = membersByTeam.get(teamNo) ?? [];
          const captain = delegationByTeam.get(teamNo);
          // Members not already assigned to this exact team (bench + other teams), excluding leader
          const addableMembers = clan.members.filter(m => m.teamNo !== teamNo && m.discordId !== clan.leaderId && m.role !== 'LEADER');
          // Captain candidates: on this team, not clan leaders
          const captainCandidates = members.filter(m => m.role !== 'LEADER');
          // Delegation-visibility audit (T4): a captain of THIS team gets
          // the "+ Add Seat" button below (server forces the new seat's
          // teamNo to match — this card already hardcodes teamNo in the
          // create call, so there's no picker to restrict).
          const canManageThisTeam = canManageTeamScoped(canManage, viewerCaptainTeams, teamNo);
          // Own-team seat/bench assignment (2026-07-21): "+ Add Player"'s
          // DESTINATION is this card's team — canAssignToTeam mirrors the
          // backend's destination-only check (source team is irrelevant, see
          // captainAccess.ts), so a captain of this team may pull in a
          // member from the bench OR from any other team, exactly like a
          // leader always could.
          const canAssignPlayersThisTeam = canAssignToTeam(canManage, viewerCaptainTeams, teamNo);

          return (
            <div key={teamNo} className="border border-iron-800/60 bg-iron-950">

              {/* Team header row */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-iron-800/40 px-4 py-2.5">
                <span className="flex items-center gap-2 font-display text-[10px] lg:text-xs font-black uppercase tracking-[0.18em] text-white">
                  <Users className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                  {(() => {
                    const tName = clan.teamNames?.[String(teamNo) as keyof typeof clan.teamNames];
                    return tName ? (
                      <>
                        <span>{tName}</span>
                        <span className="font-sans text-[9px] font-normal normal-case tracking-normal text-iron-500">
                          Team {teamNo}
                        </span>
                      </>
                    ) : (
                      `Team ${teamNo}`
                    );
                  })()}
                </span>

                {/* Captain chip or Set Captain button */}
                <div className="flex items-center gap-2">
                  {captain ? (
                    <>
                      <div className="flex items-center gap-1.5" title={captainKindLabel(captain.kind)}>
                        <Star className="h-2.5 w-2.5 shrink-0 text-amber-300" strokeWidth={2.5} />
                        <span className="font-display text-[10px] lg:text-xs font-black uppercase tracking-[0.14em] text-amber-200">
                          {resolveName(captain.granteeId, clan.members)}
                        </span>
                        {/* captainExpiryDisplay (lib/roles.ts) — the single
                            gate: PERMANENT shows nothing, COMP_SCOPED shows a
                            fixed "until comp ends" note, WINDOWED shows the
                            live countdown. */}
                        {(() => {
                          const display = captainExpiryDisplay(captain.kind, captain.expiresAt);
                          if (display.kind === 'countdown') {
                            return (
                              <span className="flex items-center gap-0.5 text-[10px] text-iron-500">
                                <Clock className="h-2.5 w-2.5" strokeWidth={2.5} />
                                {relativeExpiry(display.expiresAt)}
                              </span>
                            );
                          }
                          if (display.kind === 'until-comp-ends') {
                            return (
                              <span className="flex items-center gap-0.5 text-[10px] text-iron-500">
                                <Trophy className="h-2.5 w-2.5" strokeWidth={2.5} />
                                until comp ends
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {canManage && (
                        <>
                          {/* Extend — WINDOWED only (captainExpiryDisplay's
                              'countdown' result IS "is this a time-boxed
                              captaincy" — same check the badge above already
                              made, never re-derived). */}
                          {captainExpiryDisplay(captain.kind, captain.expiresAt).kind === 'countdown' && (
                            <div
                              className="relative"
                              ref={extendGrantId === captain.grantId ? extendMenuRef : undefined}
                            >
                              <button
                                onClick={() => setExtendGrantId(extendGrantId === captain.grantId ? null : captain.grantId)}
                                className="border border-iron-700/60 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500/50 hover:text-forge-200"
                              >
                                Extend
                              </button>
                              {extendGrantId === captain.grantId && (
                                <div className="absolute right-0 top-full z-40 mt-1 w-36 border border-forge-500/30 bg-iron-950 p-2 shadow-lg">
                                  <p className="mb-1.5 font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
                                    Extend by
                                  </p>
                                  <div className="flex flex-col gap-1">
                                    {DURATION_OPTIONS.map(opt => (
                                      <button
                                        key={opt.hours}
                                        onClick={() =>
                                          extendMut.mutate({
                                            granteeId: captain.granteeId,
                                            grantId: captain.grantId,
                                            hours: opt.hours,
                                          })
                                        }
                                        disabled={extendMut.isPending}
                                        className="border border-iron-700 bg-iron-950 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wide text-iron-300 transition-colors hover:border-forge-500/50 hover:text-forge-200 disabled:opacity-50"
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            onClick={() =>
                              revokeMut.mutate({
                                granteeId: captain.granteeId,
                                grantId: captain.grantId,
                              })
                            }
                            disabled={revokeMut.isPending}
                            className="border border-red-500/30 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-wider text-red-400 transition-colors hover:border-red-400/60 hover:text-red-300 disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    canManage && captainCandidates.length > 0 && (
                      <button
                        onClick={() =>
                          setCaptainPickerTeam(captainPickerTeam === teamNo ? null : teamNo)
                        }
                        className="flex items-center gap-1 border border-iron-700/60 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500/50 hover:text-forge-200"
                      >
                        <Star className="h-2.5 w-2.5" strokeWidth={2.5} />
                        Set Captain
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Body — member chips + controls */}
              <div className="px-4 py-3">
                {members.length > 0 ? (
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {members.map(m => (
                      <div
                        key={m.discordId}
                        className={cn(
                          'inline-flex items-center gap-1.5 border px-2.5 py-1 font-display text-[10px] lg:text-xs font-black uppercase tracking-wide',
                          m.discordId === me.discordId
                            ? 'border-forge-500/40 bg-forge-950/40 text-forge-200'
                            : 'border-iron-700/60 bg-iron-950 text-iron-200'
                        )}
                      >
                        {memberName(m)}
                        {captain && captain.granteeId === m.discordId && (
                          <span
                            className="flex items-center gap-0.5 font-sans text-[9px] font-normal normal-case tracking-normal text-amber-300"
                            title={captainKindLabel(captain.kind)}
                          >
                            <Star className="h-2.5 w-2.5" strokeWidth={2.5} />
                            {captainKindBadge(captain.kind)}
                          </span>
                        )}
                        {m.discordId === me.discordId && (
                          <span className="font-sans text-[9px] font-normal normal-case tracking-normal text-iron-500">
                            you
                          </span>
                        )}
                        {renderChipMenu(m)}
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Reserved seats — ghost/pending placeholders parked on this
                    team. No member yet; claiming (any path) auto-joins this
                    team. Styled distinctly from filled member chips. */}
                {(reservedSeatsByTeam.get(teamNo)?.length ?? 0) > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {reservedSeatsByTeam.get(teamNo)!.map(seat => (
                      <div
                        key={seat.gamertag}
                        className="inline-flex items-center gap-1.5 border border-dashed border-iron-600/70 bg-iron-950 px-2.5 py-1 font-display text-[10px] lg:text-xs font-black uppercase tracking-wide text-iron-400"
                      >
                        {seat.status === 'PENDING_CLAIM' ? (
                          <Clock className="h-3 w-3 shrink-0 text-amber-400" strokeWidth={2.5} />
                        ) : (
                          <Armchair className="h-3 w-3 shrink-0 text-iron-500" strokeWidth={2.5} />
                        )}
                        {seat.gamertag}
                        <span className="font-sans text-[9px] font-normal normal-case tracking-normal text-iron-500">
                          {seat.status === 'PENDING_CLAIM' ? 'Pending' : 'Reserved'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* + Add Player / + Add Seat buttons */}
                <div className="flex flex-wrap gap-2">
                  {canAssignPlayersThisTeam && addableMembers.length > 0 && (
                    <button
                      onClick={() => {
                        setAddPlayerTeam(addPlayerTeam === teamNo ? null : teamNo);
                        setCaptainPickerTeam(null);
                        setAddSeatTeam(null);
                      }}
                      className="flex items-center gap-1 border border-iron-700/60 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500/50 hover:text-forge-200"
                    >
                      <Plus className="h-2.5 w-2.5" strokeWidth={2.5} />
                      Add Player
                    </button>
                  )}
                  {canManageThisTeam && (
                    <button
                      onClick={() => {
                        setAddSeatTeam(addSeatTeam === teamNo ? null : teamNo);
                        setAddSeatGamertag('');
                        setAddPlayerTeam(null);
                        setCaptainPickerTeam(null);
                      }}
                      className="flex items-center gap-1 border border-dashed border-iron-700/60 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500/50 hover:text-forge-200"
                    >
                      <Armchair className="h-2.5 w-2.5" strokeWidth={2.5} />
                      Add Seat
                    </button>
                  )}
                </div>

                {/* Add player picker */}
                {addPlayerTeam === teamNo && (
                  <div className="mt-2 border border-iron-700/40 bg-iron-950">
                    <div className="flex items-center justify-between border-b border-iron-800/40 px-3 py-1.5">
                      <p className="font-display text-[9px] font-black uppercase tracking-[0.2em] text-forge-400">
                        Pick a player
                      </p>
                      <button
                        onClick={() => setAddPlayerTeam(null)}
                        className="text-iron-500 transition-colors hover:text-iron-300"
                        aria-label="Close"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="max-h-40 divide-y divide-iron-800/30 overflow-y-auto">
                      {addableMembers.map(m => (
                        <button
                          key={m.discordId}
                          onClick={() => teamMut.mutate({ discordId: m.discordId, teamNo })}
                          disabled={teamMut.isPending}
                          className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-iron-900 disabled:opacity-50"
                        >
                          <span className="text-xs lg:text-sm text-iron-100">{memberName(m)}</span>
                          <span
                            className={cn(
                              'ml-2 shrink-0 font-display text-[9px] font-black uppercase tracking-wider',
                              m.teamNo ? 'text-iron-500' : 'text-iron-500'
                            )}
                          >
                            {m.teamNo ? `Team ${m.teamNo}` : 'Bench'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add seat picker — reserve a placeholder gamertag on this team */}
                {addSeatTeam === teamNo && (
                  <div className="mt-2 border border-iron-700/40 bg-iron-950">
                    <div className="flex items-center justify-between border-b border-iron-800/40 px-3 py-1.5">
                      <p className="font-display text-[9px] font-black uppercase tracking-[0.2em] text-forge-400">
                        Reserve a seat
                      </p>
                      <button
                        onClick={() => { setAddSeatTeam(null); setAddSeatGamertag(''); }}
                        className="text-iron-500 transition-colors hover:text-iron-300"
                        aria-label="Close"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <input
                        autoFocus
                        value={addSeatGamertag}
                        onChange={e => setAddSeatGamertag(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            const trimmed = addSeatGamertag.trim();
                            if (trimmed) createSeatMut.mutate({ gamertag: trimmed, teamNo });
                          } else if (e.key === 'Escape') {
                            setAddSeatTeam(null);
                            setAddSeatGamertag('');
                          }
                        }}
                        placeholder="Gamertag#tag"
                        disabled={createSeatMut.isPending}
                        // min-w-0 — without it the input's intrinsic min-width
                        // wins over flex-1 shrink and pushes the Reserve
                        // button out of the panel on narrow mobile widths.
                        className="input min-w-0 flex-1 border border-iron-600"
                      />
                      <button
                        onClick={() => {
                          const trimmed = addSeatGamertag.trim();
                          if (trimmed) createSeatMut.mutate({ gamertag: trimmed, teamNo });
                        }}
                        disabled={!addSeatGamertag.trim() || createSeatMut.isPending}
                        className="shrink-0 border border-forge-500/60 bg-forge-950/60 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-forge-100 transition-colors hover:border-forge-400 hover:bg-forge-900/70 hover:text-white disabled:opacity-50"
                      >
                        {createSeatMut.isPending ? 'Reserving…' : 'Reserve'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Captain picker — inline member list for this team */}
                {captainPickerTeam === teamNo && !captain && (
                  <div className="mt-2 border border-forge-500/30 bg-iron-950">
                    <div className="flex items-center justify-between border-b border-iron-800/40 px-3 py-1.5">
                      <p className="font-display text-[9px] font-black uppercase tracking-[0.2em] text-forge-400">
                        Pick captain
                      </p>
                      <button
                        onClick={() => setCaptainPickerTeam(null)}
                        className="text-iron-500 transition-colors hover:text-iron-300"
                        aria-label="Close"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="divide-y divide-iron-800/30">
                      {captainCandidates.map(m => (
                        <button
                          key={m.discordId}
                          onClick={() => {
                            setCaptainPickerTeam(null);
                            setCaptainModal({ member: m, teamNo });
                          }}
                          className="flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-iron-900"
                        >
                          <span className="text-xs lg:text-sm text-iron-100">{memberName(m)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Inline "new team" draft — expands in place instead of a popup. Name
            it, pick who plays on it, create. On success it resets to the next
            team number so you can build the next team in the same spot. */}
        {canManage && draftOpen && nextTeamNo && (() => {
          const eligible = clan.members.filter(m => m.discordId !== clan.leaderId && m.role !== 'LEADER');
          const canCreate = draftName.trim().length > 0;
          const closeDraft = () => { setDraftOpen(false); setDraftName(''); setDraftIds(new Set()); };
          return (
            <div className="border border-forge-500/40 bg-iron-950">
              {/* Draft header */}
              <div className="flex items-center justify-between border-b border-forge-500/25 px-4 py-2.5">
                <span className="flex items-center gap-2 font-display text-[10px] lg:text-xs font-black uppercase tracking-[0.18em] text-forge-200">
                  <Plus className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                  New Team
                  <span className="font-sans text-[9px] font-normal normal-case tracking-normal text-iron-500">
                    Team {nextTeamNo}
                  </span>
                </span>
                <button
                  onClick={closeDraft}
                  className="text-iron-500 transition-colors hover:text-iron-300"
                  aria-label="Cancel new team"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              </div>

              {/* Draft body */}
              <div className="space-y-3 px-4 py-3">
                <div>
                  <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={draftName}
                    onChange={e => setDraftName(e.target.value)}
                    placeholder="e.g. Alpha Squad"
                    maxLength={40}
                    autoFocus
                    className="input w-full border border-iron-600"
                  />
                </div>

                <div>
                  <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
                    Add Players <span className="font-sans font-normal normal-case tracking-normal text-iron-500">(optional — {draftIds.size} selected)</span>
                  </label>
                  <p className="mb-1.5 text-[10px] leading-snug text-iron-500">
                    Optional — pick who plays on this team now, or create it empty and add players or reserved seats afterward. Everyone you leave unpicked stays in the clan on the bench.
                  </p>
                  {eligible.length > 0 ? (
                    <div className="max-h-48 divide-y divide-iron-800/30 overflow-y-auto border border-iron-700 bg-iron-950">
                      {eligible.map(m => {
                        const checked = draftIds.has(m.discordId);
                        return (
                          <button
                            key={m.discordId}
                            type="button"
                            onClick={() => {
                              setDraftIds(prev => {
                                const next = new Set(prev);
                                if (checked) next.delete(m.discordId);
                                else next.add(m.discordId);
                                return next;
                              });
                            }}
                            className={cn(
                              'flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors',
                              checked ? 'bg-forge-950/40' : 'hover:bg-iron-900'
                            )}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <span className={cn(
                                'flex h-4 w-4 shrink-0 items-center justify-center border',
                                checked ? 'border-forge-400 bg-forge-500/20 text-forge-200' : 'border-iron-600 text-transparent'
                              )}>
                                <Check className="h-3 w-3" strokeWidth={3} />
                              </span>
                              <span className={cn('truncate text-xs lg:text-sm', checked ? 'font-medium text-forge-200' : 'text-iron-200')}>
                                {memberName(m)}
                              </span>
                            </span>
                            <span className={cn(
                              'ml-2 shrink-0 font-display text-[9px] font-black uppercase tracking-wider',
                              checked ? 'text-forge-400' : m.teamNo ? 'text-iron-500' : 'text-iron-500'
                            )}>
                              {checked ? 'On team' : m.teamNo ? `Team ${m.teamNo}` : 'Bench'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="border border-iron-800/50 bg-iron-950 px-3 py-3 text-xs text-iron-500">
                      No clan members to add yet — invite players to the clan first, then build a team.
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeDraft}
                    className="flex-1 border border-iron-700 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:border-iron-500 hover:bg-iron-900 hover:text-white"
                  >
                    {usedTeamNos.length > 0 ? 'Done' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => createTeamMut.mutate()}
                    disabled={!canCreate || createTeamMut.isPending}
                    className="flex-1 border border-forge-500/60 bg-forge-950/60 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-forge-100 transition-all hover:border-forge-400 hover:bg-forge-900/70 hover:text-white disabled:opacity-50"
                  >
                    {createTeamMut.isPending ? 'Creating…' : `Create Team ${nextTeamNo}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Empty state */}
        {displayTeamNos.length === 0 && !draftOpen && (
          <div className="border border-iron-800/40 bg-iron-950 px-4 py-6 text-center">
            <p className="text-xs lg:text-sm text-iron-500">
              {canManage && nextTeamNo
                ? 'No teams yet. Click Create Team above to start, or assign players from the bench below.'
                : 'No teams yet — your leader will set these up.'}
            </p>
          </div>
        )}
      </div>

      {/* Bench */}
      {benchMembers.length > 0 && (
        <div className="border-t border-iron-800/40 pt-4">
          <h3 className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-500">
            Bench
          </h3>
          <div className="flex flex-wrap gap-2">
            {benchMembers.map(m => (
              <div
                key={m.discordId}
                className="flex items-center gap-1.5 border border-iron-700/60 bg-iron-950 px-2.5 py-1.5"
              >
                <span
                  className={cn(
                    'font-display text-[10px] lg:text-xs font-black uppercase tracking-wide',
                    m.discordId === me.discordId ? 'text-forge-200' : 'text-iron-200'
                  )}
                >
                  {memberName(m)}
                </span>
                {benchAssignableTeamNos.length > 0 && (
                  <button
                    onClick={() =>
                      setBenchAssignId(benchAssignId === m.discordId ? null : m.discordId)
                    }
                    className="text-iron-500 transition-colors hover:text-forge-300"
                    title="Assign to team"
                  >
                    <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
                  </button>
                )}
                {renderChipMenu(m)}
              </div>
            ))}
          </div>

          {/* Bench assign picker — shows below the bench row for selected member */}
          {benchAssignId && (
            <div className="mt-3 max-w-xs border border-forge-500/30 bg-iron-950 p-3">
              <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.2em] text-forge-400">
                Assign {resolveName(benchAssignId, clan.members)} — pick team
              </p>
              <div className="flex flex-wrap gap-1.5">
                {benchAssignableTeamNos.map(n => (
                  <button
                    key={n}
                    onClick={() => teamMut.mutate({ discordId: benchAssignId, teamNo: n })}
                    disabled={teamMut.isPending}
                    className="border border-iron-700 bg-iron-950 px-3 py-1.5 font-display text-[11px] font-black uppercase tracking-wide text-iron-300 transition-colors hover:border-forge-500/50 hover:text-forge-200 disabled:opacity-50"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Banned members */}
      {(clan.bannedMembers?.length ?? 0) > 0 && (
        <div className="border-t border-red-900/40 pt-4 mt-2">
          <h3 className="mb-3 font-display text-[10px] font-black uppercase tracking-[0.22em] text-red-500/80">
            Banned ({clan.bannedMembers!.length})
          </h3>
          <div className="space-y-1.5">
            {clan.bannedMembers!.map((b: BannedMember) => {
              // Banned members are no longer in clan.members, so resolveName
              // (active-roster lookup) can never find them — the backend now
              // resolves a display name for banned rows directly (GET
              // /clan/:name shaping in index.ts), so prefer that.
              const displayName = b.displayName ?? b.gamertag ?? b.discordId.slice(-6);
              return (
                <div
                  key={b.discordId}
                  className="flex items-center justify-between gap-3 border border-red-900/30 bg-red-950/20 px-3 py-2"
                >
                  <div className="min-w-0">
                    <span className="font-display text-[10px] lg:text-xs font-black uppercase tracking-wide text-red-300">
                      {displayName}
                    </span>
                    {b.reason && (
                      <p className="mt-0.5 truncate text-[10px] text-iron-500">{b.reason}</p>
                    )}
                  </div>
                  {isClanOwner && (
                    <button
                      onClick={() => unbanMut.mutate(b.discordId)}
                      disabled={unbanMut.isPending}
                      className="shrink-0 border border-iron-700/60 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500/50 hover:text-forge-200 disabled:opacity-50"
                    >
                      Unban
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Make Captain modal — opened from the team-level captain picker */}
      {captainModal && (
        <MakeCaptainModal
          member={captainModal.member}
          clanName={clan.name}
          presetTeamNo={captainModal.teamNo}
          onClose={() => setCaptainModal(null)}
          onToast={onToast}
        />
      )}

      {/* ── Remove confirm ─────────────────────────────────────────────────── */}
      {confirmRemoveMember && (
        <ConfirmModal
          tone="danger"
          icon={AlertTriangle}
          title={`Remove ${memberName(confirmRemoveMember)} from ${clan.name}?`}
          body="Their seat becomes open again and can be claimed by someone else."
          confirmLabel="Remove"
          confirmIcon={UserMinus}
          onConfirm={() => removeMut.mutate(confirmRemoveMember.discordId)}
          onCancel={() => setConfirmRemoveMember(null)}
          pending={removeMut.isPending}
        />
      )}

      {/* ── Transfer ownership confirm ──────────────────────────────────────── */}
      {confirmTransferMember && (
        <ConfirmModal
          tone="warn"
          icon={Crown}
          title={`Make ${memberName(confirmTransferMember)} the Leader of ${clan.name}?`}
          body="You'll hand over the clan and become a regular member. This can't be undone unless they transfer it back to you."
          confirmLabel="Make Leader"
          confirmIcon={Crown}
          onConfirm={() => roleMut.mutate({ discordId: confirmTransferMember.discordId, scopedRole: 'CLAN_OWNER' })}
          onCancel={() => { setConfirmTransferMember(null); setTransferAcknowledged(false); }}
          confirmDisabled={!transferAcknowledged}
          pending={roleMut.isPending}
        >
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={transferAcknowledged}
              onChange={e => setTransferAcknowledged(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-amber-400"
            />
            <span className="text-xs text-iron-300">
              I understand I'm giving up ownership and becoming a regular member of {clan.name}.
            </span>
          </label>
        </ConfirmModal>
      )}

      {/* ── Ban confirm ────────────────────────────────────────────────────── */}
      {confirmBanMember && (
        <ConfirmModal
          tone="danger"
          icon={UserX}
          title={`Ban ${memberName(confirmBanMember)} from ${clan.name}?`}
          body="They'll be removed immediately and blocked from rejoining. You can lift the ban later."
          confirmLabel="Ban"
          confirmIcon={UserX}
          onConfirm={() => banMut.mutate({ discordId: confirmBanMember.discordId, reason: banReason.trim() || undefined })}
          onCancel={() => { setConfirmBanMember(null); setBanReason(''); }}
          pending={banMut.isPending}
        >
          <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
            Reason (optional)
          </label>
          <textarea
            value={banReason}
            onChange={e => setBanReason(e.target.value)}
            placeholder="e.g. repeated unsportsmanlike conduct"
            rows={2}
            className="w-full resize-none border border-iron-700/60 bg-iron-950 px-3 py-2 text-xs text-iron-100 placeholder-iron-600 focus:border-iron-500 focus:outline-none"
          />
        </ConfirmModal>
      )}
    </div>
  );
}
